import type { AutoJoinPrefs, ZabitBridge } from '../../renderer/src/global';
import type { CalendarEventDTO, ChatThreadDetailDTO, MeetingDTO, MeetingFolderDTO, MeetingShareDTO } from '../../src/shared/types';

/**
 * YALNIZCA GELİŞTİRME: `npm run dev:web` sırasında adrese `?demo` eklenince
 * giriş yapmadan örnek verilerle arayüzü gösterir — düzeni (özellikle telefon
 * görünümünü) sunucuya bağlanmadan denemek için. Yayın paketine girmez
 * (installBridge.ts yalnızca `import.meta.env.DEV` iken kullanır).
 * Sunucuya, Supabase'e ya da tarayıcı deposuna hiçbir şey yazmaz.
 */

const now = Date.now();
const hour = 60 * 60 * 1000;

/** Sohbet geçmişi demosu: bellekte; sayfa yenilenince başa döner. */
const demoChats: ChatThreadDetailDTO[] = [
  {
    id: 'demo-chat-1',
    title: 'Kampanya toplantısında ne kararlaştırıldı?',
    createdAt: new Date(now - 26 * hour).toISOString(),
    lastMessageAt: new Date(now - 26 * hour).toISOString(),
    messageCount: 2,
    messages: [
      { id: 'demo-chat-1-a', role: 'user', text: 'Kampanya toplantısında ne kararlaştırıldı?', createdAt: new Date(now - 26 * hour).toISOString() },
      { id: 'demo-chat-1-b', role: 'assistant', text: 'Örnek yanıt: kampanya 15 Ekim’de başlıyor, landing sayfası yeniden yazılacak.', createdAt: new Date(now - 26 * hour).toISOString() },
    ],
  },
  {
    id: 'demo-chat-2',
    title: 'Açık kalan aksiyon maddelerim',
    createdAt: new Date(now - 100 * hour).toISOString(),
    lastMessageAt: new Date(now - 100 * hour).toISOString(),
    messageCount: 2,
    messages: [
      { id: 'demo-chat-2-a', role: 'user', text: 'Açık kalan aksiyon maddelerim', createdAt: new Date(now - 100 * hour).toISOString() },
      { id: 'demo-chat-2-b', role: 'assistant', text: 'Örnek yanıt: taslak metni Selin paylaşacak.', createdAt: new Date(now - 100 * hour).toISOString() },
    ],
  },
];

const WELCOME_DEMO = new URLSearchParams(window.location.search).get('demo') === 'karsilama';
let welcomeAccepted = false;
let welcomeDone = false;

function meeting(id: string, title: string, hoursAgo: number, minutes: number, space: 'private' | 'shared'): MeetingDTO {
  return {
    id,
    title,
    recordedAtISO: new Date(now - hoursAgo * hour).toISOString(),
    durationSeconds: minutes * 60,
    status: 'recorded',
    space,
    tags: ['Kayıt'],
    participants: [
      { name: 'Selin Kaya', email: 'selin@ornek.com' },
      { name: 'Mert Demir', email: 'mert@ornek.com' },
    ],
    summary: {
      tldr: 'Örnek özet: çeyrek hedefleri gözden geçirildi, yeni kampanyanın takvimi netleşti.',
      keyPoints: ['Kampanya 15 Ekim’de başlıyor', 'Bütçe %10 artırıldı'],
      decisions: ['Landing sayfası yeniden yazılacak'],
    },
    actionItems: [{ id: `${id}-a1`, text: 'Taslak metni paylaş', assignee: 'Selin', completed: false }],
    craftBlocks: [],
    transcript: [
      { id: `${id}-t1`, speaker: 'Sen', timestamp: '00:05', seconds: 5, text: 'Herkese merhaba, başlayalım mı?' },
      { id: `${id}-t2`, speaker: 'Katılımcı', timestamp: '00:12', seconds: 12, text: 'Evet, önce kampanya takvimine bakalım.' },
    ],
    storage: 'local',
  };
}

const MEETINGS: MeetingDTO[] = [
  meeting('demo-1', 'Haftalık pazarlama toplantısı', 2, 42, 'private'),
  meeting('demo-2', 'Lale Kozmetik ile tanışma', 26, 31, 'shared'),
  meeting('demo-3', 'Ürün yol haritası', 50, 58, 'private'),
  // Gerçek kimlik biçiminde: paylaşım yalnızca kaydedilmiş toplantıda açılıyor.
  meeting('00000000-0000-4000-8000-000000000004', 'Yatırımcı sunumu provası', 74, 36, 'private'),
];

const minute = 60 * 1000;

function calendarEvent(
  id: string,
  title: string,
  startsInMin: number,
  minutes: number,
  extra: Partial<CalendarEventDTO> = {}
): CalendarEventDTO {
  return {
    id,
    title,
    startISO: new Date(now + startsInMin * minute).toISOString(),
    endISO: new Date(now + (startsInMin + minutes) * minute).toISOString(),
    isAllDay: false,
    htmlLink: '',
    source: 'google',
    attendeeCount: 3,
    ...extra,
  };
}

const meet = { provider: 'meet', label: 'Google Meet', url: 'https://meet.google.com/abc-defg-hij' } as const;
const zoom = { provider: 'zoom', label: 'Zoom', url: 'https://zoom.us/j/1234567890' } as const;

/** Bot düğmelerini denemek için: kabul edilmiş, yanıtlanmamış, reddedilmiş ve linksiz toplantılar. */
const EVENTS: CalendarEventDTO[] = [
  calendarEvent('ev-now', 'Satış ekibi senkronu', -10, 40, { conference: meet, responseStatus: 'accepted' }),
  calendarEvent('ev-soon', 'Lale Kozmetik demo', 12, 30, { conference: zoom, responseStatus: 'needsAction' }),
  calendarEvent('ev-later', 'Yatırımcı görüşmesi', 180, 45, { conference: meet, responseStatus: 'declined' }),
  calendarEvent('ev-office', 'Ofis kahvesi', 240, 30, { location: 'Kadıköy ofis' }),
];

// Demo otomatik bot seçimleri — yalnızca bellekte (sayfa yenilenince sıfırlanır).
let autoJoinPrefs: AutoJoinPrefs = { autoJoinBot: true, autoJoinExcluded: [], autoJoinIncluded: [] };

// Demo paylaşım durumu — yalnızca bellekte; e-posta gitmez, bağlantı örnektir.
let demoShare: MeetingShareDTO = { access: 'restricted', link: 'https://api.talknote.net/share/demo', recipients: [] };

const demoShareApi: NonNullable<ZabitBridge['meetingShare']> = {
  get: async () => demoShare,
  setAccess: async (_id, access) => (demoShare = { ...demoShare, access }),
  addRecipients: async (_id, emails) => {
    const known = new Set(demoShare.recipients.map((recipient) => recipient.email));
    const added = emails.filter((email) => !known.has(email)).map((email) => ({ id: email, email, addedAt: new Date().toISOString(), lastViewedAt: null }));
    return (demoShare = { ...demoShare, recipients: [...demoShare.recipients, ...added] });
  },
  removeRecipient: async (_id, recipientId) => (demoShare = { ...demoShare, recipients: demoShare.recipients.filter((recipient) => recipient.id !== recipientId) }),
};

// Demo klasörleri — yalnızca bellekte.
let demoFolders: MeetingFolderDTO[] = [{ id: '00000000-0000-4000-8000-0000000000f1', name: 'Müşteri görüşmeleri' }];

const overrides: Partial<ZabitBridge> = {
  sendBot: async (request) => {
    console.info('[demo] bot gönderildi:', request.meetingUrl);
    return { meetingId: 'demo-1' };
  },
  meetingShare: demoShareApi,
  folders: {
    list: async () => demoFolders,
    create: async (name) => {
      const folder = { id: crypto.randomUUID(), name };
      demoFolders = [...demoFolders, folder];
      return folder;
    },
    rename: async (id, name) => {
      demoFolders = demoFolders.map((folder) => (folder.id === id ? { ...folder, name } : folder));
    },
    remove: async (id) => {
      demoFolders = demoFolders.filter((folder) => folder.id !== id);
      MEETINGS.forEach((item) => {
        if (item.folderId === id) item.folderId = null;
      });
    },
    moveMeeting: async (meetingId, folderId) => {
      const item = MEETINGS.find((candidate) => candidate.id === meetingId);
      if (item) item.folderId = folderId;
      console.info('[demo] klasör:', meetingId, '→', folderId);
    },
    share: demoShareApi,
  },
  getAuthState: async () => ({
    signedIn: true,
    calendarConnected: false,
    user: { id: 'demo-user', email: 'demo@talknote.net', name: 'Demo Kullanıcı', role: 'user', plan: 'pro' },
  }),
  // `?demo=karsilama`: yeni kullanıcı gibi — sözleşme onayı, Hazırsın ve ardından ürün turu.
  getOnboardingState: async () => {
    if (!WELCOME_DEMO) return { pending: [], needsSetup: false, offline: false };
    const pending = welcomeAccepted
      ? []
      : (['terms', 'privacy', 'recording', 'transfer'] as const).map((kind) => ({
          kind,
          version: '2026-10-20',
          title: { tr: kind, en: kind },
          url: kind === 'privacy' ? 'https://talknote.net/gizlilik' : 'https://talknote.net/kullanim-kosullari',
        }));
    return { pending, needsSetup: !welcomeDone, offline: false };
  },
  acceptLegal: async () => {
    welcomeAccepted = true;
  },
  completeOnboarding: async () => {
    welcomeDone = true;
  },
  getLegalLinks: async () => ({
    terms: 'https://talknote.net/kullanim-kosullari',
    privacy: 'https://talknote.net/gizlilik',
    distanceSales: 'https://talknote.net/mesafeli-satis-sozlesmesi',
    delivery: 'https://talknote.net/teslimat-ve-iade',
    disclaimer: 'https://talknote.net/sorumluluk-reddi',
    participants: 'https://talknote.net/katilimci-aydinlatma',
  }),
  listMeetings: async () => MEETINGS,
  getAppLanguage: async () => 'tr',
  getRecordingPrefs: async () => ({ transcribeLanguage: 'tr' }),
  getGoogleCalendarStatus: async () => ({ connected: true, profile: { email: 'demo@talknote.net', name: 'Demo Kullanıcı' } }),
  getUpcomingCalendarEvents: async () => EVENTS,
  getTodayCalendarEvents: async () => EVENTS,
  getOutlookStatus: async () => ({ configured: true, connected: false }),
  getSummaryEmailStatus: async () => ({ enabled: true, everyone: false, recipient: '', canSend: true }),
  getAutoJoinPrefs: async () => autoJoinPrefs,
  setAutoJoinEnabled: async (enabled) => (autoJoinPrefs = { ...autoJoinPrefs, autoJoinBot: enabled }),
  setAutoJoinEvent: async (eventId, join) => {
    const excluded = autoJoinPrefs.autoJoinExcluded.filter((id) => id !== eventId);
    const included = autoJoinPrefs.autoJoinIncluded.filter((id) => id !== eventId);
    (join ? included : excluded).push(eventId);
    return (autoJoinPrefs = { ...autoJoinPrefs, autoJoinExcluded: excluded, autoJoinIncluded: included });
  },
  getBotScheduleMap: async () => ({}),
  // Telefondaki "Daha fazla" kullanım kartı ve Genel ayarlar > profil.
  getPlanUsage: async () => ({
    plan: 'pro',
    quotaMinutes: 20 * 60,
    usedMinutes: 13 * 60 + 20,
    meetingCount: MEETINGS.length,
    periodStartISO: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
    periodEndISO: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString(),
  }),
  // Abonelik ekranı: web'den ödenmiş, 15 gün kalmış aylık Pro (plan değiştirme düğmeleri).
  getBillingStatus: async () => ({
    configured: true,
    plans: (['pro', 'plus'] as const).flatMap((plan) =>
      (['monthly', 'yearly'] as const).map((interval) => ({
        plan,
        interval,
        referenceCode: `onetime:${plan}:${interval}`,
        name: `${plan} ${interval}`,
        price: (plan === 'pro' ? 629 : 1259) * (interval === 'yearly' ? 9.6 : 1),
        currencyCode: 'TRY',
        paymentInterval: interval === 'yearly' ? 'YEARLY' : 'MONTHLY',
        paymentIntervalCount: 1,
        trialPeriodDays: 0,
        renews: false,
      }))
    ),
    subscription: {
      status: 'PREPAID',
      provider: 'iyzico-onetime',
      planReference: 'onetime:pro:monthly',
      currentPeriodEndISO: new Date(Date.now() + 15 * 86_400_000).toISOString(),
    },
  }),
  getProfile: async () => ({
    email: 'demo@talknote.net',
    displayName: 'Demo Kullanıcı',
    pronouns: '',
    department: 'product',
    jobTitle: 'manager',
    company: '',
    avatarUrl: null,
    hasPassword: false,
  }),
  listPeople: async () => [],
  listCompanies: async () => [],
  listTrash: async () => [],
  signIn: async () => ({ ok: false, error: 'Demo modunda giriş yok.' }),
  // Sohbet ve bildirim penceresini denemek için: sunucuya gitmeden örnek yanıt ve "gönderildi".
  chatWithAI: async () => 'Örnek yanıt: kampanya 15 Ekim’de başlıyor, landing sayfası yeniden yazılacak. Taslak metni Selin paylaşacak.',
  chatSend: async (threadId, text) => {
    const at = new Date().toISOString();
    let thread = demoChats.find((item) => item.id === threadId);
    const created = !thread;
    if (!thread) {
      thread = { id: `demo-chat-${Date.now()}`, title: text.trim().slice(0, 60), createdAt: at, lastMessageAt: at, messageCount: 0, messages: [] };
      demoChats.unshift(thread);
    }
    const reply = 'Örnek yanıt: kampanya 15 Ekim’de başlıyor, landing sayfası yeniden yazılacak.';
    thread.messages.push(
      { id: `${thread.id}-u${thread.messages.length}`, role: 'user', text, createdAt: at },
      { id: `${thread.id}-a${thread.messages.length + 1}`, role: 'assistant', text: reply, createdAt: at },
    );
    thread.messageCount = thread.messages.length;
    thread.lastMessageAt = at;
    demoChats.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
    return { reply, threadId: thread.id, title: created ? thread.title : null, saved: true };
  },
  listChatThreads: async () => demoChats.map(({ messages: _messages, ...thread }) => thread),
  getChatThread: async (id) => {
    const thread = demoChats.find((item) => item.id === id);
    if (!thread) throw new Error('Sohbet bulunamadı.');
    return thread;
  },
  renameChatThread: async (id, title) => {
    const thread = demoChats.find((item) => item.id === id);
    if (thread) thread.title = title.trim().slice(0, 80);
    return thread?.title ?? title;
  },
  deleteChatThread: async (id) => {
    const index = demoChats.findIndex((item) => item.id === id);
    if (index >= 0) demoChats.splice(index, 1);
  },
  deleteAllChatThreads: async () => demoChats.splice(0).length,
  reportAiContent: async () => {},
  // Web'e özgü eşzamanlı metotlar söz döndürmemeli.
  takeSignInError: () => null,
  takeCalendarConnectReturn: async () => null,
  takeRecoveredRecordings: async () => [],
  localAudioUrl: () => undefined,
  persistRecordingChunk: () => {},
  setRecordingWindowMode: () => {},
  liveAudio: () => {},
  meetingPromptAction: () => {},
};

export function createDevBridge(base: ZabitBridge): ZabitBridge {
  return new Proxy(base, {
    get(target, prop: string) {
      if (prop in overrides) return overrides[prop as keyof ZabitBridge];
      const real = target[prop as keyof ZabitBridge];
      if (typeof real !== 'function') return real;
      // Abonelikler boş; kalan her çağrı sunucuya gitmeden reddedilir.
      if (prop.startsWith('on')) return () => () => {};
      return async () => {
        throw new Error(`Demo modu: ${prop} kullanılamıyor.`);
      };
    },
  });
}
